/**
 * La medida del carnet, en milímetros. **El único sitio que la fija.**
 *
 * ⚠ **Es la ID-1 de la ISO/IEC 7810, la del carnet de banco, puesta de pie.** 85,60
 * × 53,98 mm redondeados a 86 × 54, y girados: 54 de ancho por 86 de alto. No es una
 * medida elegida por gusto — es la única que entra en las fundas y los porta-carnets
 * que se compran hechos. Un gafete de 60 × 90 «porque se ve mejor» obliga a mandar a
 * hacer las fundas.
 *
 * ⚠ **De pie y no acostado.** El carnet corporativo cuelga de un cordón, y colgado se
 * lee en vertical: la foto arriba y el nombre debajo. Acostado hay que girar la
 * cabeza o el carnet, que es lo que hace que la gente acabe llevándolo del revés.
 *
 * ⚠ **Las décimas se redondean al milímetro, y eso es deliberado.** La norma dice
 * 53,98; la guillotina de una oficina no corta décimas y la funda tiene holgura. Lo
 * que no se puede es que la medida dependa de la fuente o del zoom, que es de lo que
 * habla `rotulacion/medidas.ts`: aquí es un número y el CSS lo declara en `mm`.
 */
export const CARNET = { ancho: 54, alto: 86 } as const

/**
 * El retrato: un círculo de este diámetro, en mm, contando el aro, y lo menos a lo
 * que puede encoger.
 *
 * ⚠ **Redondo, como en el diseño pedido, y recortado con `object-fit: cover`.** Lo que
 * sube quien rellena la ficha suele ser una foto de teléfono; el círculo se queda con
 * la cara y deja fuera los hombros y el fondo de la oficina. Deformarla —el `fill` por
 * defecto— es lo único que no se puede hacer con una cara.
 *
 * ⚠ **Es lo único de la cara que ENCOGE.** Con un nombre en dos líneas, un cargo que
 * no cabe en una y un correo partido, el texto pisaría la ola del pie; el retrato cede
 * lo que haga falta hasta `minimo`, y en una ficha corriente se queda en `diametro`.
 */
export const RETRATO = { diametro: 26, minimo: 20 } as const

/** El aire entre el corte y lo escrito, en mm. Más cerca, la guillotina se lo come. */
export const MARGEN = 4

/**
 * Lo que se reserva abajo para el pie del frente, en mm: la ola, el sello del
 * aniversario con el nombre de la agencia y el QR.
 */
export const PIE = 20

/**
 * El lado del QR, en mm, sin su recuadro blanco.
 *
 * ⚠ **10 mm bastan porque lleva sólo la web.** Una dirección corta sale en la versión 2
 * del QR, de 25 módulos por lado: a 10 mm cada uno mide 0,4, holgado para cualquier
 * teléfono aunque la tarjeta esté plastificada. Si algún día lleva más texto, crece
 * de versión y hay que volver a hacer la cuenta.
 */
export const QR = 10

/**
 * El hueco entre la cara y el reverso cuando se pintan juntos, en mm.
 *
 * ⚠ **Lo necesita también la pantalla, para contar cuántos carnets caben.** Con el
 * reverso, la pieza que se repite en la hoja no mide 54 mm: mide 54 + este hueco +
 * 54. Calculando con 54 la pantalla decía «9 por hoja» y salían 3, porque sólo cabe
 * una columna de 112 mm.
 */
export const SEPARACION_CARAS = 4
